import React, { useRef, useState, useEffect } from 'react'

import { Field, Formik, ErrorMessage } from 'formik'
import * as yup from 'yup'

import errorIcon from 'images/error-icon.svg'

import ProductOverview from './parts/ProductOverview'
import StarRatingInput from './parts/StarRatingInput'
import SampleReview from './parts/SampleReview'

import { renderTextInput, renderTextArea } from './formik-util'
import loadExternalLibrary from 'util/loadExternalLibrary'
import { getRandomString } from 'util/maths'

const reviewValidation = yup.object().shape({
  rating: yup
    .number()
    .min(1, 'Please specify a rating')
    .max(5, 'Please specify a rating'),
  headline: yup.string().required('Headline is required'),
  comments: yup.string().required('Filling in comments is required'),
  usernickname: yup.string().required('A nickname is required'),
  useremail: yup
    .string()
    .required('An email address is required')
    .email('You should enter a valid email address'),
})

/*

The weird thing with this form is that we have to inject a browser fingerprint that gets generated by a 3rd party script

*/

const SubmitProductReviewScreen = ({
  review,
  defaultProductId,
  onFormSubmit,
}) => {
  console.log('SubmitProductReviewScreen render')
  defaultProductId = defaultProductId
    ? defaultProductId
    : review.products[0].productId

  const [productId, setProductId] = useState(defaultProductId)
  const formRef = useRef()
  const sessionIdRef = useRef(getRandomString(22)) // reviewSessionId tells our API if we're re-submitting the same form of not
  // so we need to maintain the same ID even if we re-render

  useEffect(() => {
    if (typeof window != 'undefined') {
      window.IGLOO = window.IGLOO || {
        // if we specify a callback (which we need to interact with formik), the bbout_element_id field is ignored
        // "bbout_element_id" : "ioBlackBox",    // Populate #ioBlackBox input with device fingerprint
        enable_rip: true, // Enable Real IP protection.
        enable_flash: false, // Disable flash
        install_flash: false, // Don't ask user to install flash
        loader: {
          version: 'general5', // Non-experimental 5.x updates
          fp_static: false, // Don't load 1st party resources
        },
      }

      window.IGLOO.bb_callback = (blackBoxString, isComplete) => {
        // console.log(`${blackBoxString}`)

        // manually trigger the field's change so formik picks it up
        // document.querySelector('input[name=ioBlackBox]').value = blackBoxString;
        // document.querySelector('#ioBlackBox').change()
        // document.querySelector('input[name=ioBlackBox]').change()

        if (formRef && formRef.current) {
          formRef.current.setFieldValue('ioBlackBox', blackBoxString)
        }
      }

      loadExternalLibrary({
        url: '/js/iovation_loader_5x.js',
        name: 'iovation',
      }).then(function () {
        // console.log("IOVATION LOADED")
      })
    }
  }, [])

  let product = review.products.find(product => {
    return product.productId === productId
  })

  const handleProductSelect = (id, label) => {
    setProductId(id)
  }

  if (!product) {
    product = review.products[0]
  }

  // formik
  let initialValues = {
    productId: productId,
    rating: -1,
    headline: '',
    comments: '',
    usernickname: '',
    useremail: '',
    ioBlackBox: '',
    reviewSessionId: sessionIdRef.current,
    nonFieldError: '',
  }

  return (
    <div className={`product-review`}>
      <div className="review-capture">
        <h1>{review.productReviewTitle}</h1>
        {/* <div className='selector-header' dangerouslySetInnerHTML={{__html: review.productSelectorDescription}} /> */}

        <ProductOverview
          products={review.products}
          defaultProductId={productId}
          onProductSelect={handleProductSelect}
        />

        <form>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validateOnBlur={true}
            validateOnChange={true}
            validationSchema={reviewValidation}
            onSubmit={async (
              values,
              { setSubmitting, setFieldError, isSubmitting },
            ) => {
              if (isSubmitting) {
                return
              }
              // console.log("onSubmit")
              // console.log(values)
              // handleFormSubmit(values['productId'], values['rating'], values['headline'], values['comments'], values['usernickname'], values['ioBlackBox'], values['reviewSessionId'])
              if (onFormSubmit) {
                setSubmitting(true)
                let result = await onFormSubmit(
                  values['productId'],
                  values['rating'],
                  values['headline'],
                  values['comments'],
                  values['usernickname'],
                  values['useremail'],
                  values['ioBlackBox'],
                  values['reviewSessionId'],
                )

                setSubmitting(false)
                if (result) {
                  /*
                                    We got an error back. These (unfortunately) take 3 different shapes depending on where it fails

                                    1. sometimes we get back an array of {Message/Code}s:
                                        [{"Message":"The passKey provided is invalid.","Code":"ERROR_PARAM_INVALID_API_KEY"}]

                                    2. sometimes we get back this HUGE glob of detail
                                    {
                                        "timestamp":1607112335161,
                                        "status":400,
                                        "error":"Bad Request",
                                        "errors":[
                                            {
                                                "codes":[
                                                    "NotEmpty.reviewRequest.fingerprint",
                                                    "NotEmpty.fingerprint",
                                                    "NotEmpty.java.lang.String",
                                                    "NotEmpty"
                                                ],
                                                "arguments":[
                                                    {
                                                        "codes": ["reviewRequest.fingerprint","fingerprint"],
                                                        "arguments":null,
                                                        "defaultMessage":"fingerprint",
                                                        "code":"fingerprint"
                                                    }],
                                                "defaultMessage":"must not be empty",
                                                "objectName":"reviewRequest",
                                                "field":"fingerprint",
                                                "rejectedValue":"",
                                                "bindingFailure":false,
                                                "code":"NotEmpty"
                                            },
                                            {
                                                "codes":[
                                                    "NotEmpty.reviewRequest.rating",
                                                    "NotEmpty.rating",
                                                    "NotEmpty.java.lang.String",
                                                    "NotEmpty"
                                                ],
                                                "arguments":[
                                                    {
                                                        "codes":[ "reviewRequest.rating", "rating"],
                                                        "arguments":null,"defaultMessage":"rating","code":"rating"}
                                                    ],
                                                "defaultMessage":"Rating must be a whole number between 1 and 5.",
                                                "objectName":"reviewRequest",
                                                "field":"rating",
                                                "rejectedValue":null,
                                                "bindingFailure":false,
                                                "code":"NotEmpty"
                                            }],
                                            "message":"Validation failed for object='reviewRequest'. Error count: 2","path":"/review/submit/"
                                        }

                                    3. sometimes we get a map
                                        (this may be the case that our API caught a BV error and formetted it?)
                                        {
                                            "usernickname": {
                                                "Field":"usernickname",
                                                "Message":"Your nickname can only contain letters and numbers.",
                                                "Code":"ERROR_FORM_PATTERN_MISMATCH"
                                            }
                                        }

                                */
                  if (Array.isArray(result)) {
                    // error type 1
                    result.forEach(item => {
                      // formick doesn't have a concept of non-field errors
                      // so we invent a field to associate these with
                      setFieldError('commonformerror', item.Message)
                    })
                  } else if (result.errors) {
                    // error type 2
                    result.errors.forEach(item => {
                      setFieldError(item.field, item.defaultMessage)
                      setFieldError(
                        'commonformerror',
                        item.field + ' ' + item.defaultMessage,
                      ) // also show this at the bottom in case they complain about something else
                    })
                  } else {
                    // error type 3
                    for (let field in result) {
                      console.log(`${field}`)
                      try {
                        setFieldError(field, result[field].Message)
                      } catch (e) {
                        // catches mal-formed responses (no Message)
                        console.warn(e)
                        setFieldError(
                          field,
                          'Unhandled error with this information.',
                        )
                      }
                    }
                  }
                }
              }
            }}
            innerRef={formRef}
          >
            {formik => {
              return (
                <>
                  <h2
                    dangerouslySetInnerHTML={{
                      __html: `What did you think of ${product.title}`,
                    }}
                  />
                  {/* <Field name="ioBlackBox" key="ioBlackBox">
                                    <input key={'ioBlackBox'} type="hidden" name='ioBlackBox' id={`ioBlackBox`} onChange={(e) => {
                                                    console.log(e.target)
                                                    setFieldValue('ioBlackBox', e.target.value)
                                                } } />

                                </Field> */}
                  <Field key={`ioBlackBox`} name={`ioBlackBox`} type="hidden" />
                  <Field name="rating" key="rating">
                    {({
                      field, // { name, value, onChange, onBlur }
                      form: { touched, errors, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
                      meta,
                    }) => (
                      <>
                        <div className="field">
                          <StarRatingInput
                            value={field.value}
                            name={field.name}
                            onStarClick={(value, name, e) => {
                              console.log(`onStarClick(${name}, ${value})`)
                              // this.setState({rating: value})
                              // field.onChange(value)
                              setFieldValue(name, value, false)
                            }}
                          />
                          {errors['rating'] && touched && (
                            <div className="error">
                              <img
                                className="error-icon"
                                src={errorIcon}
                                alt=""
                              />
                              {errors['rating']}
                              {/* <ErrorMessage name={field.name} />TODO: <-- isn't right. Had to add line above */}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </Field>
                  <br />
                  <Field
                    key={`headline`}
                    name={`headline`}
                    type="text"
                    label={`Review Headline *`}
                    placeholder={`Review Headline *`}
                    component={renderTextInput}
                    fullWidth={true}
                    error={formik.errors[`headline`]}
                    touched={formik.touched[`headline`] && true}
                  />
                  <Field
                    key={`comments`}
                    name={`comments`}
                    type="text"
                    label={`Review Comments *`}
                    placeholder={`Review Comments *`}
                    component={renderTextArea}
                    error={formik.errors[`comments`]}
                    touched={formik.touched[`comments`] && true}
                  />
                  <div className={`nickname-email-container`}>
                    <Field
                      key={`usernickname`}
                      name={`usernickname`}
                      type="email"
                      label={`Nickname *`}
                      placeholder={`Nickname *`}
                      component={renderTextInput}
                      fullWidth={false}
                      error={formik.errors[`usernickname`]}
                      touched={formik.touched[`usernickname`] && true}
                    />
                    <Field
                      key={`useremail`}
                      name={`useremail`}
                      type="email"
                      label={`Email *`}
                      placeholder={`Email *`}
                      component={renderTextInput}
                      fullWidth={false}
                      error={formik.errors[`useremail`]}
                      touched={formik.touched[`useremail`] && true}
                    />
                  </div>
                  <br />
                  <p className="small">
                    Nickname may be published along with your review. Please do
                    not use your email address or full name.
                  </p>
                  <br />
                  {formik.errors['commonformerror'] && (
                    <div className="field">
                      <p className="error">
                        Error: {formik.errors['commonformerror']}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    className={'complete'}
                    disabled={formik.isSubmitting || !formik.isValid}
                    onClick={formik.handleSubmit}
                  >
                    Submit
                  </button>
                </>
              )
            }}
          </Formik>
        </form>
      </div>

      {/* <select className='product-list'>
                {
                    review.products.map((product, i) => {
                        return (
                            <div className={`product clickable`} onClick={(e) => {
                                    onSelect(product.id)
                                }}>
                                <PlaceHolderImage src={legacyImageURL(product.imageId)} alt={product.dropdownTitle} width={100} height={100} />
                                <h2 dangerouslySetInnerHTML={{__html: product.title}} />
                            </div>
                        )
                    })
                }
            </select> */}

      {product.sampleReviews && (
        <div className="sample-reviews">
          <h3 className="title">Other Customer Reviews</h3>
          <div className="sample-review-list">
            {product.sampleReviews.map((sampleReview, i) => {
              return (
                <SampleReview
                  key={i}
                  title={sampleReview.title}
                  displayDate={sampleReview.created}
                  reviewBody={sampleReview.detail}
                  nickname={sampleReview.nickname}
                  rating={sampleReview.rating}
                  outOf={5}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default SubmitProductReviewScreen
